"use client"
import { useState, useMemo } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { ChevronDown, Search, HelpCircle, Star, MessageCircle, Clock, Shield, Zap, X } from "lucide-react"
import Header from "../components/Header"
import { Link } from "react-router-dom"
import Footer from "../components/Footer"

export const Faqs = () => {
  const pageBnrImg = {
    position: "relative",
    backgroundImage: `url('./media/frontend/img/about/blog/1920x700/banner-2.jpg')`,
    backgroundPosition: "center",
    backgroundSize: "cover",
  }

  const accordionData = [
    {
      title: "How do I place an order for food on Shefs Cloud?",
      content:
        "Select your favorite meals, add them to the cart, and checkout once you have entered your location. Place your order online and wait for your meal to arrive.",
      category: "ordering",
      icon: MessageCircle,
    },
    {
      title: "On Shefs Cloud, what kind of food can I order?",
      content:
        "There are lots of homemade dishes to choose from, including traditional meals, snacks, baked goods, and desserts. Whether you're craving biryani, pasta, desi curries, or freshly baked goods, Shefs Cloud's got it!",
      category: "food",
      icon: Star,
    },
    {
      title: "Is Shefs Cloud a grocery delivery service?",
      content: "No . Its a portal for Home shefs where shefs can register and get orders for delivery.",
      category: "service",
      icon: HelpCircle,
    },
    {
      title: "Is Shefs Cloud available 24/7?",
      content:
        "Shefs Cloud operates with flexible delivery hours based on home shefs availability in your area. However, you can check active home shefs by entering your location.",
      category: "service",
      icon: Clock,
    },
    {
      title: "Can I pay cash for Shefs Cloud orders?",
      content: "Yes, you can pay cash on delivery for your Shefs Cloud orders.",
      category: "payment",
      icon: Shield,
    },
    {
      title: "Does Shefs Cloud offer online payment options?",
      content: "You can securely pay online using your credit/debit card or digital wallets.",
      category: "payment",
      icon: Shield,
    },
    {
      title: "Can I order food for someone else?",
      content:
        "Yes! You can place an order for friends, family, or colleagues. Just update their name and delivery address at checkout.",
      category: "ordering",
      icon: MessageCircle,
    },
    {
      title: "How much does Shefs Cloud charge for delivery?",
      content:
        "Delivery charges depend on your location and the home shef you are ordering from. You can see the delivery fee while finalizing your order.",
      category: "delivery",
      icon: Zap,
    },
    {
      title: "Is there a minimum order value?",
      content: (
        <div>
          <p>
            Some home shefs may have a minimum order requirement, which will be displayed during the ordering process.
          </p>
        </div>
      ),
      category: "ordering",
      icon: MessageCircle,
    },
    {
      title: "Enjoy Fresh, Homemade Meals with Shefs Cloud",
      content: (
        <div>
          <p>
            Say a big no to unhealthy fast food and expensive restaurants. Shefs Cloud delivers nutritious, home-cooked
            meals and frozen food delivered quickly and affordably. Place your order now and savour the taste of home!
          </p>
        </div>
      ),
      category: "general",
      icon: Star,
    },
  ]

  const [expandedIndex, setExpandedIndex] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("all")

  const categories = [
    { id: "all", label: "All Questions", icon: HelpCircle },
    { id: "ordering", label: "Ordering", icon: MessageCircle },
    { id: "food", label: "Food Options", icon: Star },
    { id: "service", label: "Service", icon: Clock },
    { id: "payment", label: "Payment", icon: Shield },
    { id: "delivery", label: "Delivery", icon: Zap },
    { id: "general", label: "General", icon: HelpCircle },
  ]

  const filteredData = useMemo(() => {
    return accordionData.filter((item) => {
      const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesCategory = selectedCategory === "all" || item.category === selectedCategory
      return matchesSearch && matchesCategory
    })
  }, [searchTerm, selectedCategory])

  const handleToggle = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index)
  }

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: [0.6, -0.05, 0.01, 0.99] },
  }

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  const scaleIn = {
    initial: { opacity: 0, scale: 0.8 },
    animate: { opacity: 1, scale: 1 },
    transition: { duration: 0.5, ease: "easeOut" },
  }

  return (
    <>
      <Header />

      {/* Enhanced Hero Section */}
      <section className="relative overflow-hidden">
        <div className="pageBanr py-32 relative" style={pageBnrImg}>
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/80"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black/70"></div>

          {/* Animated background elements */}
          <div className="absolute inset-0 overflow-hidden">
            <motion.div
              className="absolute top-20 left-10 w-2 h-2 bg-primary rounded-full"
              animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY }}
            />
            <motion.div
              className="absolute top-40 right-20 w-3 h-3 bg-white rounded-full"
              animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.8, 0.3] }}
              transition={{ duration: 3, repeat: Number.POSITIVE_INFINITY, delay: 1 }}
            />
            <motion.div
              className="absolute bottom-32 left-1/4 w-1 h-1 bg-primary rounded-full"
              animate={{ scale: [1, 2, 1], opacity: [0.4, 1, 0.4] }}
              transition={{ duration: 2.5, repeat: Number.POSITIVE_INFINITY, delay: 0.5 }}
            />
          </div>

          <motion.div
            className="container mx-auto relative z-[1] text-center px-4"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            <motion.div
              className="inline-block mb-6"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.8, delay: 0.5, type: "spring" }}
            >
              <div className="w-20 h-20 bg-gradient-to-br from-primary/20 to-primary/40 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/20">
                <HelpCircle className="w-10 h-10 text-primary" />
              </div>
            </motion.div>

            <motion.h2
              className="font-bold text-5xl md:text-7xl text-white uppercase tracking-wider mb-6 bg-gradient-to-r from-white via-gray-100 to-white bg-clip-text"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              FAQs
            </motion.h2>

            <motion.div
              className="w-32 h-1 bg-gradient-to-r from-transparent via-primary to-transparent mx-auto mb-8"
              initial={{ width: 0 }}
              animate={{ width: 128 }}
              transition={{ duration: 1, delay: 0.8 }}
            />

            <motion.ul
              className="inline-flex items-center space-x-2 text-white mb-0 bg-black/20 backdrop-blur-sm rounded-full px-6 py-3 border border-white/10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1 }}
            >
              <li className="text-lg">
                <Link
                  to="/"
                  className="!text-white hover:!text-primary transition-all duration-300 hover:scale-110 inline-block"
                >
                  Home
                </Link>
              </li>
              <li className="text-white/60">/</li>
              <li className="text-lg font-semibold text-primary">FAQs</li>
            </motion.ul>

            <motion.p
              className="text-xl text-white/90 mt-6 max-w-2xl mx-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 1.2 }}
            >
              Find answers to all your questions about Shefs Cloud
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Enhanced FAQ Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 via-white to-gray-50 relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-20 left-10 w-64 h-64 bg-gradient-to-br from-primary/5 to-transparent rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-80 h-80 bg-gradient-to-br from-primary/3 to-transparent rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          {/* Search and Filter Section */}
          <motion.div
            className="max-w-4xl mx-auto mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <div className="text-center mb-12">
              <motion.div className="inline-flex items-center space-x-3 mb-6" variants={scaleIn}>
                <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-primary/20 rounded-xl flex items-center justify-center">
                  <HelpCircle className="w-6 h-6 text-primary" />
                </div>
                <span className="text-primary font-semibold text-lg uppercase tracking-wider">Get Help</span>
              </motion.div>

              <h1 className="font-bold text-4xl lg:text-5xl uppercase tracking-wider mb-6">
                Frequently Asked{" "}
                <span className="bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                  Questions
                </span>
              </h1>
              <div className="w-24 h-1 bg-gradient-to-r from-primary via-primary/60 to-transparent mx-auto mb-8" />
            </div>

            {/* Search Bar */}
            <motion.div className="relative mb-8" variants={fadeInUp}>
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search your questions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-12 py-4 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-2xl shadow-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300 text-lg"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            </motion.div>

            {/* Category Filter */}
            <motion.div className="flex flex-wrap justify-center gap-3" variants={staggerContainer}>
              {categories.map((category) => {
                const IconComponent = category.icon
                return (
                  <motion.button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`inline-flex items-center space-x-2 px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                      selectedCategory === category.id
                        ? "bg-gradient-to-r from-primary to-primary/80 text-white shadow-lg shadow-primary/25"
                        : "bg-white/80 backdrop-blur-sm text-gray-700 hover:bg-primary/10 hover:text-primary border border-gray-200"
                    }`}
                    variants={fadeInUp}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <IconComponent className="w-4 h-4" />
                    <span>{category.label}</span>
                  </motion.button>
                )
              })}
            </motion.div>
          </motion.div>

          {/* FAQ Accordion */}
          <motion.div
            className="max-w-4xl mx-auto"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <AnimatePresence mode="wait">
              {filteredData.length > 0 ? (
                <motion.div className="space-y-6" variants={staggerContainer}>
                  {filteredData.map((section, index) => {
                    const IconComponent = section.icon
                    const isExpanded = expandedIndex === index
                    return (
                      <motion.div
                        key={index}
                        className="group"
                        variants={fadeInUp}
                        layout
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3 }}
                      >
                        <motion.div
                          className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-lg hover:shadow-xl transition-all duration-500 border border-white/50 overflow-hidden"
                          whileHover={{ y: -5 }}
                        >
                          <motion.div
                            onClick={() => handleToggle(index)}
                            className="flex items-center justify-between p-8 cursor-pointer group-hover:bg-gradient-to-r group-hover:from-primary/5 group-hover:to-transparent transition-all duration-300"
                            whileHover={{ backgroundColor: "rgba(0,0,0,0.02)" }}
                          >
                            <div className="flex items-center space-x-4 flex-1">
                              <motion.div
                                className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-300 ${
                                  isExpanded
                                    ? "bg-gradient-to-br from-primary to-primary/80 text-white shadow-lg"
                                    : "bg-gradient-to-br from-primary/10 to-primary/20 text-primary group-hover:from-primary/20 group-hover:to-primary/30"
                                }`}
                                animate={{ rotate: isExpanded ? 360 : 0 }}
                                transition={{ duration: 0.5 }}
                              >
                                <IconComponent className="w-6 h-6" />
                              </motion.div>
                              <h3 className="text-xl font-bold text-gray-800 group-hover:text-primary transition-colors duration-300 flex-1 text-left">
                                {section.title}
                              </h3>
                            </div>
                            <motion.div
                              className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                                isExpanded
                                  ? "bg-primary text-white"
                                  : "bg-gray-100 text-gray-600 group-hover:bg-primary/10 group-hover:text-primary"
                              }`}
                              animate={{ rotate: isExpanded ? 180 : 0 }}
                              transition={{ duration: 0.3 }}
                            >
                              <ChevronDown className="w-5 h-5" />
                            </motion.div>
                          </motion.div>

                          <AnimatePresence>
                            {isExpanded && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                                className="overflow-hidden"
                              >
                                <motion.div
                                  className="px-8 pb-8"
                                  initial={{ y: -20 }}
                                  animate={{ y: 0 }}
                                  exit={{ y: -20 }}
                                  transition={{ duration: 0.3, delay: 0.1 }}
                                >
                                  <div className="w-full h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent mb-6" />
                                  <div className="bg-gradient-to-br from-gray-50/50 to-white/50 rounded-2xl p-6 border border-gray-100">
                                    <div className="text-lg text-gray-700 leading-relaxed">
                                      {typeof section.content === "string" ? <p>{section.content}</p> : section.content}
                                    </div>
                                  </div>
                                </motion.div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </motion.div>
                      </motion.div>
                    )
                  })}
                </motion.div>
              ) : (
                <motion.div
                  className="text-center py-16"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Search className="w-12 h-12 text-gray-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-600 mb-4">No questions found</h3>
                  <p className="text-gray-500 text-lg">
                    Try adjusting your search terms or selecting a different category.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Contact Support Section */}
          <motion.div
            className="max-w-4xl mx-auto mt-20"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <div className="bg-gradient-to-br from-primary/5 via-white to-primary/5 rounded-3xl p-12 border border-primary/10 text-center">
              <motion.div
                className="w-16 h-16 bg-gradient-to-br from-primary to-primary/80 rounded-2xl flex items-center justify-center mx-auto mb-6"
                animate={{
                  y: [-5, 5, -5],
                }}
                transition={{
                  duration: 3,
                  repeat: Number.POSITIVE_INFINITY,
                  ease: "easeInOut",
                }}
              >
                <MessageCircle className="w-8 h-8 text-white" />
              </motion.div>
              <h3 className="text-3xl font-bold text-gray-800 mb-4">Still have questions?</h3>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                Can't find the answer you're looking for? Our support team is here to help you 24/7.
              </p>
              <motion.button
  onClick={() => {
    const email = "info@shefscloud.com";
    const subject = encodeURIComponent("Support Request – Shefs Cloud");
    const body = encodeURIComponent(`Hello Shefs Cloud Support Team,

I need assistance with...

Thank you.`);

    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${email}&su=${subject}&body=${body}`;

    window.open(gmailUrl, "_blank") || (window.location.href = `mailto:${email}?subject=${subject}&body=${body}`);
  }}
  className="inline-flex items-center space-x-3 bg-gradient-to-r from-primary to-primary/80 text-white px-8 py-4 rounded-full font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
  whileHover={{ scale: 1.05, y: -2 }}
  whileTap={{ scale: 0.95 }}
>
  <MessageCircle className="w-5 h-5" />
  <span>Contact Support</span>
</motion.button>

            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </>
  )
}

export default Faqs
